import classNames from 'classnames';
import { useState, useEffect, Suspense } from 'react';
import { useSelector } from 'react-redux';
import {
  Outlet, useLocation, useNavigate,
} from 'react-router-dom';
import {
  Navbar, Container, NavbarBrand, NavbarToggler, Nav, NavItem,
  // NavLink,
  Collapse, DropdownToggle, UncontrolledDropdown,
  DropdownMenu, DropdownItem,
  // Row, Col,
} from 'reactstrap';

import { IRootState } from '@app/store';
import ReforalLogo from '@assets/img/reforal-logo.svg';
import navMenus from '@config/navbar';
import LocaleService from '@services/LocaleService';
import { CustomButton } from '@shared/buttons';
import NotificationAlert from '@shared/components/notification-alert/NotificationAlert';
import NotificationDot from '@shared/components/notification-dot/NotificationDot';
import useNotificationChecker from '@shared/hooks/useNotificationStatus';
import {
  ProfileIcon,
  // ReforalIcon,
  CustomDropdownCaretIcon,
} from '@shared/icons';
import Loading from '@shared/utils/Loading/Loading';

import NotificationButton from './components/NotificationButton';
import DashboardNavDrawer from './components/dashboard-nav-drawer/DashboardNavDrawer';
import ProfileMenu from './components/profile-menu/ProfileMenu';
import TrialExpiredGate from './components/trial-expired-gate/TrialExpiredGate';

import './DashboardLayout.scss';

// Define a type for user type list
type UserTypeList = {
  dentist: string;
  'dental-clinic': string;
  laboratory: string;
};

// Define UserType with a union of keys of UserTypeList
type UserType = keyof UserTypeList;

const DashboardLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const userImage = useSelector((state: IRootState) => state.Auth?.user?.image);
  const userType = useSelector((state: IRootState) => state.Auth?.user?.type) as UserType;
  const trialEndsAt = useSelector((state: IRootState) => (state.Auth?.user as any)?.trial_ends_at);
  const subscription = useSelector((state: IRootState) => (state.Auth?.user as any)?.subscription);

  // Routes where a post-trial clinic can take action to subscribe — the
  // TrialExpiredGate must not render here, otherwise the modal traps the user
  // on the very page (My Plans & Payments) we just pointed them at.
  const isOnSubscribeActionRoute = location.pathname.startsWith('/packages-pricing')
    || location.pathname.startsWith('/plans-payments');

  // Treat the user as actively subscribed when the subscription row exists,
  // is not cancelled (cancelled_at is now exposed by AuthClientSubscriptionResource),
  // and has both an id and a plan_id. Relying on `!subscription?.plan_id` alone
  // surfaces the gate for partially-populated payloads (e.g. a subscription
  // record briefly visible while the backend is mid-write).
  const hasActiveSubscription = !!subscription?.id
    && !!subscription?.plan_id
    && !subscription?.cancelled_at;

  const isTrialExpired = userType === 'dental-clinic'
    && trialEndsAt
    && new Date(trialEndsAt) <= new Date()
    && !hasActiveSubscription
    && !isOnSubscribeActionRoute;

  const hasNotification = useNotificationChecker();

  const isVerify = location.pathname.endsWith('verify');
  const [ isOpen, setIsOpen ] = useState(false);
  const [ activeLink, setActiveLink ] = useState<string | null>(location.pathname);
  const [ forcedHighlight, setForcedHighlight ] = useState<string | null>(null);
  const i18nGeneral = LocaleService.getTranslations('general');
  const i18n = LocaleService.getTranslations('navigation');

  const RIGHT_NAV = [
    {
      title: i18n.button.profile,
      icon: !userImage ? <ProfileIcon /> : <img className="user-profile" src={userImage} alt="User Profile" />,
    },
  ];

  const toggle = (event: any) => {
    event?.preventDefault();
    setIsOpen(!isOpen);
  };

  const onClickNav = (data: any) => {
    setActiveLink(data.to);
    setForcedHighlight(null);
    navigate(data.to, {
      state: {
        name: '',
        id: '',
        clinicName: '',
        clinicId: '',
      },
    });
  };

  useEffect(() => {
    if (location.state?.highlight === false) {
      setForcedHighlight(null);
      setActiveLink(null);
    } else {
      setForcedHighlight(null);
      setActiveLink(location.pathname);
    }
  }, [ location.pathname, location.state ]);

  return (
    <Container fluid className="dashboard-layout">
      {/* TopNav */}
      <Navbar
        expand="lg"
        className={classNames('custom-navbar', {
          'custom-navbar-fixed': location.pathname === '/community-board',
        })}
      >
        <NavbarBrand className="custom-navbar-brand" href={isVerify ? '/verify' : '/'}>
          <div
            className="title-container"
          >
            <span>
              {/* <ReforalIcon width="50" height="auto" /> */}
              <img src={ReforalLogo} alt="Reforal Logo" width="50px" />
            </span>
            <span className="title">
              {i18nGeneral.header.title}
            </span>
          </div>
        </NavbarBrand>
        <div className="d-flex">
          {/* <NotificationButton /> */}
          <Nav className="right-nav hide-right-nav-smaller-devices" navbar>
            {RIGHT_NAV.map((nav) => (
              nav.title === i18n.button.profile ? (
                <UncontrolledDropdown nav inNavbar key={nav.title}>
                  <DropdownToggle nav>
                    <CustomButton className="nav-button b2" color="link">
                      {nav.icon}
                    </CustomButton>
                  </DropdownToggle>
                  <ProfileMenu />
                </UncontrolledDropdown>
              ) : (
                <NavItem key={nav.title}>
                  {nav.title === i18n.button.notifications ? <NotificationButton /> : (
                    <CustomButton className="nav-button" color="link" title={nav.title}>
                      {nav.icon}
                    </CustomButton>
                  )}
                </NavItem>
              )
            ))}
          </Nav>
          <NavbarToggler onClick={toggle} />
        </div>
        {!isVerify && (
          <Collapse
            isOpen={false}
            navbar
            className="justify-content-center"
          >
            <Nav className="left-nav" navbar>
              {navMenus && userType && navMenus[userType].map((nav) => (
                <NavItem
                  key={nav.title}
                  // onClick={() => setActiveLink(nav.to)}
                  onClick={() => onClickNav(nav)}
                >

                  <CustomButton
                    className={classNames('nav-button', {
                      active: forcedHighlight === nav.to
                        || (!forcedHighlight && activeLink === nav.to)
                        || (!forcedHighlight && activeLink?.startsWith(nav.key) && nav.to !== '/'),
                    })}
                    // onClick={() => {
                    //   setForcedHighlight(null);
                    //   navigate(nav.to, {
                    //     state: {
                    //       name: '',
                    //       id: '',
                    //       clinicName: '',
                    //       clinicId: '',
                    //     },
                    //   });
                    // }}
                    color="link"
                  >
                    <span className="position-relative">
                      {nav.title}
                    </span>
                  </CustomButton>
                  {hasNotification(nav.key) && <NotificationDot />}
                </NavItem>
              ))}
            </Nav>
          </Collapse>
        )}
        {!isVerify && userType !== 'laboratory' && (
          <UncontrolledDropdown className="custom-send-dropdown">
            <DropdownToggle className="custom-dropdown-toggle">
              <span className="label">{i18n.button.send}</span><CustomDropdownCaretIcon />
            </DropdownToggle>
            <DropdownMenu className="custom-send-dropdown-menu">
              <DropdownItem
                className="first-custom-send-dropdown-item"
                onClick={() => navigate('/send/referral', {
                  state: {
                    // Keep existing state
                    ...location?.state,
                    // Add returnTo property
                    returnTo: location?.pathname,
                  },
                })}
              >
                {i18n.button.referral}
              </DropdownItem>
              <DropdownItem
                className="second-custom-send-dropdown-item"
                onClick={() => navigate('/send/lab-orders', {
                  state: {
                    // Keep existing state
                    ...location?.state,
                    // Add returnTo property
                    returnTo: location?.pathname,
                  },
                })}
              >
                {i18n.button.labOrder}
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        )}
        <Nav className="right-nav hide-right-nav" navbar>
          {RIGHT_NAV.map((nav) => (
            nav.title === i18n.button.profile ? (
              <UncontrolledDropdown nav inNavbar key={nav.title}>
                <DropdownToggle nav>
                  <CustomButton className="nav-button b2" color="link">
                    {nav.icon}
                  </CustomButton>
                </DropdownToggle>
                <ProfileMenu />
              </UncontrolledDropdown>
            ) : (
              <NavItem key={nav.title}>
                {nav.title === i18n.button.notifications ? <NotificationButton /> : (
                  <CustomButton className="nav-button" color="link" title={nav.title}>
                    {nav.icon}
                  </CustomButton>
                )}
              </NavItem>
            )
          ))}
        </Nav>
      </Navbar>
      <NotificationAlert />
      <DashboardNavDrawer
        isOpen={isOpen}
        toggleDrawer={toggle}
      />
      {isTrialExpired && <TrialExpiredGate />}
      <div className="body-container">
        <Suspense fallback={<Loading show cover={false} />}>
          <Outlet />
        </Suspense>
      </div>
    </Container>
  );
};

export default DashboardLayout;
